import { db, storage, appCheck } from "./firebase";
import { getToken } from "firebase/app-check";
import {
  collection,
  query,
  where,
  doc,
  updateDoc,
  getDocs,
  addDoc,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { withTimeout } from "./utils";

export async function getLists(collection_name = "lists") {
  try {
    const querySnapshot = await getDocs(query(collection(db, collection_name)));
    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error getting lists: ", error);
  }
}

export async function createList(listData, collection_name = "lists") {
  try {
    const docRef = await addDoc(collection(db, collection_name), listData);
    console.log("List created with ID: ", docRef.id);
    return docRef.id;
  } catch (error) {
    console.error("Error creating list: ", error);
    throw error;
  }
}

async function requestNarrations(url, options, signal, timeoutMs) {
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (signal?.aborted) abort();
  signal?.addEventListener("abort", abort);
  let timedOut = false;
  const timeout = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  try {
    let token;
    try {
      ({ token } = await withTimeout(getToken(appCheck), 10_000));
    } catch (error) {
      console.error("[Narrations] App Check failed before the API request", {
        code: error.code || error.name,
      });
      throw new Error("Couldn’t verify this browser. Please refresh and try again.");
    }
    const response = await fetch(url, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        "X-Firebase-AppCheck": token,
      },
      signal: controller.signal,
    });
    let data;
    try {
      data = await response.json();
    } catch {
      throw new Error("The narration service returned an unexpected response.");
    }
    if (!data || typeof data !== "object" || Array.isArray(data)) {
      throw new Error("The narration service returned an unexpected response.");
    }
    if (!response.ok) {
      console.error("[Narrations] API request failed", {
        status: response.status,
        requestId: data.requestId,
      });
      throw new Error(data.error || "Couldn’t prepare narrations. Please retry.");
    }
    return data;
  } catch (error) {
    if (timedOut) {
      throw new Error("The narration request took too long. Please retry; completed recordings are saved.");
    }
    if (error instanceof TypeError) {
      throw new Error("Couldn’t reach the narration service. Please check your connection and retry.");
    }
    throw error;
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener("abort", abort);
  }
}

export async function getListNarrations(listId, signal) {
  const data = await requestNarrations(
    `/api/generateNarrations?listId=${encodeURIComponent(listId)}`,
    { method: "GET" }, signal, 30_000,
  );
  if (!data.audioUrls || typeof data.audioUrls !== "object" || Array.isArray(data.audioUrls)) {
    throw new Error("The narration service returned an unexpected response.");
  }
  return data.audioUrls;
}

export async function generateBookNarration(listId, bookId, signal) {
  const data = await requestNarrations("/api/generateNarrations", {
    method: "POST",
    body: JSON.stringify({ listId, bookId }),
  }, signal, 110_000);
  if (data.bookId !== bookId || typeof data.audioUrl !== "string" || !data.audioUrl) {
    throw new Error("The narration service returned an unexpected response.");
  }
  return data.audioUrl;
}

export async function getListByCreated(created, collection_name = "lists") {
  try {
    const q = query(
      collection(db, collection_name),
      where("created", "==", created)
    );
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0].data();
      return { id: querySnapshot.docs[0].id, ...doc };
    } else {
      throw new Error("List not found");
    }
  } catch (error) {
    console.error("Error fetching list: ", error);
  }
}

export async function patchList(
  created,
  updatedAttrs,
  collection_name = "lists"
) {
  try {
    const q = query(
      collection(db, collection_name),
      where("created", "==", created)
    );
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const docRef = doc(db, collection_name, querySnapshot.docs[0].id);
      await updateDoc(docRef, updatedAttrs);
      console.log("List updated");
    } else {
      throw new Error("List not found");
    }
  } catch (error) {
    console.error("Error updating list: ", error);
  }
}

export async function getRankingsByCreated(
  created,
  collection_name = "rankings"
) {
  const collectionRef = collection(db, collection_name);

  const q = query(collectionRef, where("created", "==", created));

  try {
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error("Error getting documents: ", error);
  }
}

export async function pushRankings(rankingsData, collection_name = "rankings") {
  try {
    const listToPushTo = await getListByCreated(rankingsData["created"]);
    if (listToPushTo.closed) {
      console.log("Rankings not pushed because of closed list.");
      return;
    }
    const docRef = await addDoc(collection(db, collection_name), rankingsData);
    console.log("Rankings created with ID: ", docRef.id);
  } catch (error) {
    console.error("Error creating list: ", error);
  }
}

export async function searchBooks(query, searchIn = "intitle") {
  try {
    const response = await fetch("/api/searchBooks", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        query: query,
        searchIn: searchIn,
      }),
    });

    if (!response.ok) {
      throw new Error("Response not ok");
    }

    const data = await response.json();
    return data.items || [];
  } catch (error) {
    console.error("Error:", error);
  }
}

export async function askOpenai(description, name, author) {
  try {
    const question = `After querying an online API for the description of a book called ${name} by ${author}, I got the description that I pasted below. After reading it, can you give me a new description based on this one, but that is around 5 sentences? Also, please note that the text below might have some weird formatting or html. Please don't make anything up about the book and use only the description as your source. Also, only respond with the new description, do not add any other word in your reply. "${description}"`;
    const response = await fetch("/api/openai", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ question }),
    });

    if (!response.ok) {
      throw new Error("Response not ok");
    }

    const data = await response.json();
    return data.message.content;
  } catch (error) {
    console.error("Error:", error);
  }
}

export async function uploadBookClubPhoto(imageFile, timeoutMs = 15000) {
  try {
    const timestamp = Date.now();
    const filename = `${timestamp}_${imageFile.name}`;
    const storageRef = ref(storage, `book-club-photos/${filename}`);

    const snapshot = await withTimeout(
      uploadBytes(storageRef, imageFile),
      timeoutMs
    );
    console.log("Photo uploaded successfully!");

    const downloadURL = await withTimeout(getDownloadURL(snapshot.ref), 5000);
    return downloadURL;
  } catch (error) {
    console.error("Error uploading photo: ", error);
    throw error;
  }
}

export async function deleteBookClubPhoto(photoURL, timeoutMs = 5000) {
  try {
    const photoRef = ref(storage, photoURL);
    await withTimeout(deleteObject(photoRef), timeoutMs);
    console.log("Photo deleted successfully!");
  } catch (error) {
    console.error("Error deleting photo: ", error);
    throw error;
  }
}

export async function getBookClubPhotoURL(photoPath, timeoutMs = 5000) {
  try {
    const photoRef = ref(storage, photoPath);
    const downloadURL = await withTimeout(getDownloadURL(photoRef), timeoutMs);
    return downloadURL;
  } catch (error) {
    console.error("Error getting photo URL: ", error);
    throw error;
  }
}
